@file:OptIn(ExperimentalTime::class)

package com.macrofocus.high_d.axis.group

import com.macrofocus.common.crossplatform.CPHelper
import com.macrofocus.common.filter.MutableFilter
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.PropertyEvent
import com.macrofocus.common.properties.PropertyListener
import com.macrofocus.common.properties.SimpleProperty
import com.macrofocus.common.selection.Selection
import com.macrofocus.common.selection.SingleSelection
import com.macrofocus.high_d.axis.AxisFactory
import com.macrofocus.high_d.axis.AxisListener
import com.macrofocus.high_d.axis.AxisModel
import com.macrofocus.order.DefaultVisibleOrder
import com.macrofocus.order.MutableVisibleOrder
import com.macrofocus.order.OrderEvent
import com.macrofocus.order.OrderListener
import org.molap.dataframe.DataFrame
import org.molap.subset.SubsetDataFrame
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

class DefaultAxisGroupModel<R, C> private constructor(override val name: String) : AbstractAxisGroupModel<R, C>(),
    MutableAxisGroupModel<R, C> {
    override var isCollapsible = false
        set(collapsible) {
            if (isCollapsible != collapsible) {
                field = collapsible
                fireAxisChanged()
            }
        }
    private val collapsedProperty: MutableProperty<Boolean> = SimpleProperty<Boolean>(false)
    override var axisOrder: MutableVisibleOrder<AxisModel<R, C>>? = null
    private val axisListener: AxisListener = object : AxisListener {
        override fun axisChanged() {
            fireAxisChanged()
        }
    }

    constructor(name: String, vararg axisModels: AxisModel<R, C>) : this(name) {
        axisOrder = DefaultVisibleOrder<AxisModel<R, C>>(*axisModels)
        axisOrder!!.addOrderListener(object : OrderListener<AxisModel<R, C>> {
            override fun orderChanged(event: OrderEvent<AxisModel<R, C>>?) {}
            override fun orderVisibility(event: OrderEvent<AxisModel<R, C>>) {}
            override fun orderAdded(event: OrderEvent<AxisModel<R, C>>) {
                for (axisModel in event.affected) {
                    axisModel.addAxisListener(axisListener)
                }
            }

            override fun orderRemoved(event: OrderEvent<AxisModel<R, C>>) {
                for (axisModel in event.affected) {
                    axisModel.removeAxisListener(axisListener)
                }
            }
        })
        for (axisModel in axisModels) {
            axisModel.addAxisListener(axisListener)
        }
    }

    constructor(
        name: String,
        dataFrame: DataFrame<R, C, *>,
        annotationDataFrame: DataFrame<*, C, *>?,
        probing: SingleSelection<R>,
        selection: Selection<R>,
        filter: MutableFilter<R>
    ) : this(name) {
        val groupContent: ArrayList<AxisModel<R, C>> = ArrayList<AxisModel<R, C>>()
        val axisFactory: AxisFactory<R, C> = AxisFactory(dataFrame, SubsetDataFrame(dataFrame), annotationDataFrame, probing, selection, filter)
        for (c in dataFrame.columns()) {
            val axisModel: AxisModel<R, C>? = axisFactory.getAxisModel(c)
            if (axisModel != null) {
                axisModel.addAxisListener(axisListener)
                groupContent.add(axisModel)
            }
        }
        axisOrder = DefaultVisibleOrder<AxisModel<R, C>>(*groupContent.toTypedArray())
        var count = 0
        for (axisModel in groupContent) {
            if (count < 10) {
                if (CPHelper.instance.isNumericType(axisModel.type) && !axisModel.scaledInterval.isDegenerate) {
                    count++
                } else if (CPHelper.instance.isAssignableFrom(Instant::class, axisModel.type) && !axisModel.interval.isDegenerate) {
                    count++
                } else {
                    axisOrder!!.setVisible(axisModel, false)
                }
            } else {
                axisOrder!!.setVisible(axisModel, true)
            }
        }
    }

    override fun collapsedProperty(): MutableProperty<Boolean> {
        return collapsedProperty
    }

    override var isCollapsed: Boolean
        get() = collapsedProperty.value
        set(value) {this.collapsedProperty.value = value}

    override fun add(axisModel: AxisModel<R, C>) {
        axisOrder!!.add(axisModel)
    }

    override fun remove(axisModel: AxisModel<R, C>) {
        axisOrder!!.remove(axisModel)
    }

    override fun getAxisModel(name: String?): AxisModel<R, C>? {
        for (axisModel in axisOrder!!.iterableAll()) {
            if (name == axisModel.name) {
                return axisModel
            }
        }
        return null
    }

    override val axisModels: Iterable<AxisModel<R, C>>
        get() = axisOrder!!.iterableAll()

    override val visibleAxisCount: Int
        get() = axisOrder!!.size()

    override fun toString(): String {
        return "Group{" +
                "name='" + name + '\'' +
                '}'
    }

    init {
        collapsedProperty.addPropertyListener(object : PropertyListener<Boolean> {
            override fun propertyChanged(event: PropertyEvent<Boolean>) {
                fireAxisChanged()
            }
        })
    }
}